/**
 * Cliente HTTP do workspace pericial (EW.F3.T07 + E8.F5.T07). // ux-neutrality:allow doc-comment
 *
 * Espelha apps/api/types/case_workspace.py e // ux-neutrality:allow doc-comment
 * apps/api/runtime/case_workspace.py. Polling REST (sem SSE). // ux-neutrality:allow doc-comment
 *
 * E8.F5: resposta agora é `{analyses: CaseAnalysisData[]}` (K análises, // ux-neutrality:allow doc-comment
 * uma por contestada). Casos K=1 retornam lista com 1 elemento. // ux-neutrality:allow doc-comment
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';

export type CaseAnalysisStatus = 'pending' | 'running' | 'completed' | 'failed';

/**
 * Status por critério na banda canônica [1/3, 3] (E25.F3 / ADR-0035). // ux-neutrality:allow doc-comment
 * Espelha `apps/api/types/forensic.py::CriterionBandStatus` — resolvido pelo // ux-neutrality:allow doc-comment
 * backend; o front nunca deriva status de LR (lição F-E16.F2-01). // ux-neutrality:allow doc-comment
 */
export type CriterionBandStatus = 'convergent' | 'divergent' | 'neutral';

export interface PatternAnalysisData {
  id: string;
  signature_id: string;
  lr_score: number;
  lr_by_criterion: Record<string, number>;
  criteria_evidence: Record<string, string> | null;
  created_at: string;
}

/**
 * Instabilidade do conjunto de referência (E16.F2). // ux-neutrality:allow doc-comment
 * Espelha `apps/api/types/case_workspace.py::ReferenceSetInstability`. // ux-neutrality:allow doc-comment
 */
export interface ReferenceSetInstabilityData {
  unstable: boolean;
  n_patterns: number;
  criteria_in_conflict: string[];
}

/**
 * Marcador geométrico de um critério sobre a questionada (E23.F10 / ADR-0027). // ux-neutrality:allow doc-comment
 * Coordenadas em pixels naturais da imagem. `point`: x/y é o centro. `region`: // ux-neutrality:allow doc-comment
 * x/y é o canto superior esquerdo + width/height. // ux-neutrality:allow doc-comment
 */
export interface CriterionMarkerData {
  kind: 'point' | 'region';
  x: number;
  y: number;
  width: number | null;
  height: number | null;
}

export interface CriterionLocalizationData {
  criterion_key: string;
  markers: CriterionMarkerData[];
}

/**
 * Marcação por critério ancorada à questionada (E23.F10 / ADR-0027). // ux-neutrality:allow doc-comment
 * Só os critérios LOCALIZÁVEIS aparecem; globais são tratados como selo. // ux-neutrality:allow doc-comment
 */
export interface QuestionedLocalizationsData {
  image_width: number;
  image_height: number;
  criteria: CriterionLocalizationData[];
}

/**
 * Estado calculado-vs-ajustado do LR de uma análise (E33.F1/F2, ADR-0044). // ux-neutrality:allow doc-comment
 * Espelha `apps/api/types/lr_adjustment.py::LrAdjustmentState`. A mesa usa para // ux-neutrality:allow doc-comment
 * o selo "ajustado pelo perito" + exibir o valor calculado pelo motor ao lado do // ux-neutrality:allow doc-comment
 * efetivo. Quando nada foi ajustado: `composite_adjusted=false`, `adjusted_criteria={}`. // ux-neutrality:allow doc-comment
 */
export interface LrAdjustmentStateData {
  composite_adjusted: boolean;
  // LR total calculado pelo motor (preenchido só quando há override do total).
  composite_computed: number | null;
  // criterion_key -> LR calculado pelo motor (só os critérios com override).
  adjusted_criteria: Record<string, number>;
  last_adjusted_by: string | null;
}

export interface CaseAnalysisData {
  id: string;
  case_id: string;
  contested_signature_id: string;
  status: CaseAnalysisStatus;
  composite_lr: number | null;
  criteria_breakdown: Record<string, number> | null;
  agent_summary: string | null;
  error_reason: string | null;
  patterns: PatternAnalysisData[];
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  reference_set_instability: ReferenceSetInstabilityData | null;
  // E16.F2.C01: direção verbal resolvida pelo backend (banda + instabilidade).
  // Fonte única para o LRMeter — `null` quando não há composto.
  composite_lr_direction: 'h1' | 'h2' | 'inconclusive' | null;
  // E16.F6/ADR-0020: força verbal resolvida pelo backend (mesma régua do laudo).
  // O LRMeter só mapeia o rótulo — sem buckets locais. `null` na banda
  // inconclusiva, instável, ou sem composto.
  composite_lr_strength: 'limitada' | 'moderada' | 'forte' | null;
  // E23.F4/ADR-0022: intensidade de suporte 0–100% (leitura SECUNDÁRIA da
  // magnitude do LR; NÃO é probabilidade). Resolvida pelo backend. `0` na banda
  // de não-suporte; `null` quando instável ou sem composto. O front só exibe.
  composite_lr_support_intensity: number | null;
  // E39.F1/ADR-0054: índice de suporte ASSINADO −100..+100 (régua de BANCADA do
  // perito, leitura PRINCIPAL na mesa) + nível verbal. Sinal = direção, módulo =
  // força (teto LR=100 → ±100), banda [1/3, 3] → 0; instável → 0/inconclusivo.
  // Resolvidos no backend (single-source); `null` só sem composto. NÃO sai no
  // laudo (ADR-0053). Substituem a leitura de intensidade na mesa.
  composite_lr_support_index: number | null;
  composite_lr_support_level: 'inconclusivo' | 'fraco' | 'moderado' | 'forte' | null;
  // E23.F5/ADR-0023: por critério, 'evaluated' vs 'no_signal' (sem sinal
  // extraível — só os 4 dinâmicos gated). Resolvido pelo backend; o front só
  // oculta/colapsa os 'no_signal'. `null` quando não há patterns.
  criteria_signal: Record<string, 'evaluated' | 'no_signal'> | null;
  // E25.F3/ADR-0035: status por critério na banda canônica [1/3, 3]
  // (convergent ⇔ LR > 3; divergent ⇔ LR < 1/3; neutral na banda). Resolvido
  // pelo backend — fonte única para o badge do painel E a cor do marcador; o
  // front NÃO deriva status de LR. `null` sem breakdown.
  criteria_status: Record<string, CriterionBandStatus> | null;
  // E23.F10/ADR-0027: marcação por critério localizável sobre a questionada
  // (centróides de mínimos, extremidades de traço, laços, vãos, zonas).
  // Persistida no processamento da análise; `null` em análises legadas/
  // não-completed. O front desenha os marcadores e os colore pelo status de
  // `criteria_status` (E25.F3/ADR-0035).
  questioned_localizations: QuestionedLocalizationsData | null;
  // E25.F2/ADR-0034 (decisão F) + E25.F3: guard de padrão com extração
  // degradada — quando `degraded`, o workspace exibe o banner de qualidade do
  // material. `null` em análises legadas/não-completed.
  pattern_degradation: PatternDegradationData | null;
  // E33.F1/F2/ADR-0044: estado do ajuste manual de LR pelo perito (calculado
  // vs efetivo, selos da mesa). O `composite_lr`/`criteria_breakdown` acima já
  // chegam EFETIVOS (override aplicado no backend); este campo só informa o que
  // foi ajustado, para os selos. `null` em análises legadas/não-completed.
  lr_adjustment: LrAdjustmentStateData | null;
  // E36.F5/ADR-0050: a narrativa desta análise está sendo regenerada em
  // background após um ajuste manual de LR. Derivado no backend (não persistido).
  // `true` → a UI mostra "texto sendo atualizado…" e bloqueia aprovar/baixar o
  // laudo até concluir e a narrativa ser re-aprovada. Opcional (flag transiente;
  // o backend sempre envia, fixtures/respostas legadas podem omitir → falso).
  narrative_regenerating?: boolean;
  // E38.F2: visibilidade das marcações do sistema (marcadores por critério na
  // contestada). Persistida por análise (`case_analyses.show_system_markers`),
  // default `false`/oculto. A mesa inicializa o toggle daqui; o laudo só desenha
  // os marcadores quando `true`. Opcional (respostas/fixtures legadas → falso).
  show_system_markers?: boolean;
}

/**
 * Guard de padrão com qualidade de captura insuficiente (E25.F2 / ADR-0034 F). // ux-neutrality:allow doc-comment
 * Espelha `apps/api/types/case_workspace.py::PatternDegradation`. // ux-neutrality:allow doc-comment
 */
export interface PatternDegradationData {
  degraded: boolean;
  n_patterns: number;
  n_patterns_degraded: number;
  affected_criteria: string[];
}

export interface CaseAnalysisStatusData {
  analysis_id: string;
  contested_signature_id: string;
  status: CaseAnalysisStatus;
  completed_at: string | null;
}

/**
 * Aviso advisório de divergência de proporção entre crops (E15.F4). // ux-neutrality:allow doc-comment
 * Espelha `apps/api/types/case_workspace.py::ProportionWarning`. // ux-neutrality:allow doc-comment
 */
export interface ProportionWarningData {
  warned: boolean;
  max_ratio: number;
  threshold: number;
}

export interface CaseWorkspaceResponseData {
  analyses: CaseAnalysisData[];
  proportion_warning: ProportionWarningData | null;
}

/** Workspace + aviso advisório, já desempacotado para a UI (E15.F4). */
export interface WorkspaceWithWarning {
  analyses: CaseAnalysisData[];
  proportionWarning: ProportionWarningData | null;
}

export interface CaseWorkspaceStatusResponseData {
  analyses: CaseAnalysisStatusData[];
}

export type WorkspaceErrorReason =
  | 'case_not_found'
  | 'not_ready'
  | 'analysis_not_found'
  | 'network'
  | 'unknown';

export class WorkspaceError extends Error {
  public readonly reason: WorkspaceErrorReason;

  constructor(reason: WorkspaceErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'WorkspaceError';
  }
}

const HTTP_NOT_FOUND = 404;
const HTTP_UNPROCESSABLE = 422;

function isNetworkError(err: AxiosError): boolean {
  return err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED';
}

function detailString(err: AxiosError): string {
  const data = err.response?.data as { detail?: unknown } | undefined;
  return typeof data?.detail === 'string' ? data.detail : '';
}

export function classifyWorkspaceError(err: unknown): WorkspaceError {
  if (!axios.isAxiosError(err)) return new WorkspaceError('unknown');
  const axiosErr = err as AxiosError;
  const httpStatus = axiosErr.response?.status;
  const detail = detailString(axiosErr);
  if (httpStatus === HTTP_NOT_FOUND) {
    if (detail === 'CASE_NOT_FOUND') return new WorkspaceError('case_not_found');
    return new WorkspaceError('analysis_not_found');
  }
  if (httpStatus === HTTP_UNPROCESSABLE && detail === 'CASE_NOT_READY_FOR_WORKSPACE') {
    return new WorkspaceError('not_ready');
  }
  if (isNetworkError(axiosErr)) return new WorkspaceError('network');
  return new WorkspaceError('unknown');
}

async function requestWorkspace(
  client: AxiosInstance,
  caseId: string,
): Promise<CaseWorkspaceResponseData> {
  try {
    const { data } = await client.get<CaseWorkspaceResponseData>(`/casos/${caseId}/workspace`);
    return data;
  } catch (err) {
    throw classifyWorkspaceError(err);
  }
}

export async function getWorkspace(
  client: AxiosInstance,
  caseId: string,
): Promise<CaseAnalysisData[]> {
  return (await requestWorkspace(client, caseId)).analyses;
}

/**
 * Variante que também devolve o aviso de proporção (E15.F4). Usada pelo // ux-neutrality:allow doc-comment
 * workspace; o laudo segue com `getWorkspace` (só precisa das análises). // ux-neutrality:allow doc-comment
 */
export async function getWorkspaceWithWarning(
  client: AxiosInstance,
  caseId: string,
): Promise<WorkspaceWithWarning> {
  const data = await requestWorkspace(client, caseId);
  return { analyses: data.analyses, proportionWarning: data.proportion_warning };
}

export async function pollWorkspaceStatus(
  client: AxiosInstance,
  caseId: string,
): Promise<CaseAnalysisStatusData[]> {
  try {
    const { data } = await client.get<CaseWorkspaceStatusResponseData>(
      `/casos/${caseId}/workspace/status`,
    );
    return data.analyses;
  } catch (err) {
    throw classifyWorkspaceError(err);
  }
}
